import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import AuthModal from "@/components/AuthModal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const LLM_STATUS_LABEL = {
  green: "Tous les LLMs primary OK",
  yellow: "Au moins un fallback actif",
  red: "Au moins un mode en circuit ouvert",
};

const MODE_LABELS = {
  casual: { dot: "🟢", label: "Casual" },
  street: { dot: "🔴", label: "Street" },
  business: { dot: "🔵", label: "Business" },
};

function formatShort(ts) {
  const now = new Date();
  const d = new Date(ts);
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
  const diffMs = now - d;
  const day = 24 * 3600 * 1000;
  if (diffMs < 2 * day) return "hier";
  const days = Math.floor(diffMs / day);
  if (days < 7) return `${days}j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function groupKey(ts) {
  const now = new Date();
  const d = new Date(ts);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 3600 * 1000;
  const sevenDaysAgo = startOfToday - 7 * 24 * 3600 * 1000;
  if (ts >= startOfToday) return "Aujourd'hui";
  if (ts >= startOfYesterday) return "Hier";
  if (ts >= sevenDaysAgo) return "7 derniers jours";
  return "Plus ancien";
}

const GROUP_ORDER = ["Aujourd'hui", "Hier", "7 derniers jours", "Plus ancien"];

export default function Sidebar({
  conversations,
  activeId,
  onSelect,
  onCreate,
  onDelete,
  onClearAll,
  manifest,
  mobileOpen,
  onCloseMobile,
  desktopCollapsed,
  // ⚙️ Settings panel — controls hoisted from the chat header (v1.6.0)
  mode,
  onModeChange,
  voiceOutputEnabled,
  onVoiceOutputToggle,
  webSearchMode,
  onWebSearchModeChange,
  continuousMode,
  onContinuousModeToggle,
  responseLang,
  onResponseLangChange,
  // v2.19.22h — Theme toggle déplacé du header vers le drawer.
  theme,
  onThemeToggle,
  // v2.19.22h — Auth control déplacé du header vers le drawer.
  AuthControl,
}) {
  const [confirmClear, setConfirmClear] = useState(false);
  const [llmStatus, setLlmStatus] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/llm/health/public`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setLlmStatus(j.global_status || null);
      } catch (_) { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Group + sort
  const sorted = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt);
  const groups = {};
  for (const c of sorted) {
    const k = groupKey(c.updatedAt);
    if (!groups[k]) groups[k] = [];
    groups[k].push(c);
  }

  return (
    <>
      {mobileOpen && (
        <div
          className="civ-sidebar-backdrop"
          onClick={onCloseMobile}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`civ-sidebar ${mobileOpen ? "civ-sidebar-open" : ""} ${desktopCollapsed ? "is-collapsed-desktop" : ""}`}
        data-testid="sidebar"
      >
        {/* v2.19.22h — En-tête AYA dans le drawer */}
        <div className="civ-sidebar-aya-brand" data-testid="sidebar-aya-brand">
          <img
            src="/aya/splash.png"
            alt="AYA"
            className="civ-sidebar-aya-logo"
            loading="eager"
          />
          <div className="civ-sidebar-aya-text">
            <div className="civ-sidebar-aya-title">AYA</div>
            <div className="civ-sidebar-aya-sub">Assistant AYA</div>
          </div>
          {/* v2.19.23a — Bouton ✕ pour fermer le drawer (mobile uniquement via CSS) */}
          <button
            type="button"
            className="civ-sidebar-close-btn"
            onClick={() => onCloseMobile?.()}
            aria-label="Fermer le menu"
            data-testid="sidebar-close-btn"
          >
            ✕
          </button>
        </div>

        <div className="civ-sidebar-top">
          <button
            type="button"
            className="civ-new-conv-btn"
            onClick={() => {
              onCreate();
              onCloseMobile?.();
            }}
            data-testid="new-conversation-btn"
          >
            <span className="civ-plus">+</span> Nouvelle discussion
          </button>
          <a
            href="/media"
            data-testid="sidebar-media-hub-link"
            className="civ-media-hub-link"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 12px', marginTop: 8,
              borderRadius: 12, border: '1px solid rgba(52,211,153,0.25)',
              background: 'rgba(15,23,42,0.6)',
              color: '#a7f3d0', fontSize: 13, textDecoration: 'none',
              transition: 'all .15s',
            }}
            onClick={() => onCloseMobile?.()}
          >
            <span style={{ fontSize: 16 }}>📰</span>
            <span style={{ flex: 1 }}>Kiosque AYA</span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>→</span>
          </a>
        </div>

        <div className="civ-sidebar-list" data-testid="conversations-list">
          {sorted.length === 0 && (
            <div className="civ-sidebar-empty">Aucune discussion</div>
          )}
          {GROUP_ORDER.map((label) => {
            const items = groups[label];
            if (!items || items.length === 0) return null;
            return (
              <div key={label} className="civ-sidebar-group">
                <div className="civ-sidebar-group-label">{label}</div>
                {items.map((c) => {
                  const isActive = c.id === activeId;
                  const ml = MODE_LABELS[c.mode] || MODE_LABELS.casual;
                  return (
                    <div
                      key={c.id}
                      className={`civ-sidebar-item ${isActive ? "is-active" : ""}`}
                      onClick={() => {
                        onSelect(c.id);
                        onCloseMobile?.();
                      }}
                      data-testid={`conversation-item-${c.id}`}
                    >
                      <div className="civ-sidebar-item-main">
                        <span
                          className="civ-sidebar-item-dot"
                          title={ml.label}
                          aria-hidden="true"
                        >
                          {ml.dot}
                        </span>
                        <span className="civ-sidebar-item-title">
                          {c.title || "Nouvelle discussion"}
                        </span>
                      </div>
                      <div className="civ-sidebar-item-meta">
                        <span className="civ-sidebar-item-ts">
                          {formatShort(c.updatedAt)}
                        </span>
                        <button
                          type="button"
                          className="civ-sidebar-item-del"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm("Supprimer cette discussion ?")) {
                              onDelete(c.id);
                            }
                          }}
                          aria-label="Supprimer"
                          data-testid={`conversation-delete-${c.id}`}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="civ-sidebar-footer">
          {/* v2.19.29b — SidebarSettingsPanel retiré du drawer pour épurer.
              Les paramètres avancés sont désormais accessibles via /mon-compte > Paramètres. */}
          {confirmClear ? (
            <div className="civ-sidebar-confirm">
              <span>Tout effacer ?</span>
              <button
                type="button"
                className="civ-sidebar-confirm-yes"
                onClick={() => {
                  onClearAll();
                  setConfirmClear(false);
                }}
                data-testid="clear-all-confirm"
              >
                Oui
              </button>
              <button
                type="button"
                className="civ-sidebar-confirm-no"
                onClick={() => setConfirmClear(false)}
                data-testid="clear-all-cancel"
              >
                Non
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="civ-sidebar-clearall"
              onClick={() => setConfirmClear(true)}
              data-testid="clear-all-btn"
            >
              🗑️ Effacer tout
            </button>
          )}
          {manifest && (
            <span
              className="civ-gov-badge civ-gov-badge-sidebar"
              title={`Governance layer active — v${manifest.version}`}
              data-testid="governance-badge"
            >
              🛡️ JSONFIRST v{(manifest.version || "").split("-")[0]}
            </span>
          )}
          {/* v2.19.29d — Fusion Mon Compte / Connexion en un seul bouton conditionnel
              + toggle thème compact (switch iOS) ~36px. Slot AuthControl externe retiré. */}
          <AuthEntryButton />
          {onThemeToggle && (
            <ThemeToggleCompact theme={theme} onToggle={onThemeToggle} />
          )}
          {llmStatus && (
            <a
              href="/ops"
              className="civ-llm-status-badge"
              title={`État LLMs : ${LLM_STATUS_LABEL[llmStatus] || llmStatus}. Voir Ops → Santé LLM.`}
              data-testid="llm-status-icon"
              data-status={llmStatus}
            >
              🧠 {llmStatus === "green" ? "🟢" : llmStatus === "yellow" ? "🟡" : "🔴"}
            </a>
          )}
        </div>
      </aside>
    </>
  );
}


// v2.19.29d — Bouton unique compte/connexion (fusion drawer).
// - Auth : "👤 Mon Compte" + initiale display_name → /mon-compte
// - Anon : "👤 Connexion" → ouvre AuthModal
function AuthEntryButton() {
  const { user, isAuthenticated } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  const initial = (() => {
    const n = (user?.display_name || "").trim();
    if (n) return n[0].toUpperCase();
    return null;
  })();

  if (isAuthenticated) {
    return (
      <a
        href="/mon-compte"
        data-testid="sidebar-link-mon-compte"
        className="civ-sidebar-account-btn aya-account-btn-auth"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 12px", borderRadius: 10,
          textDecoration: "none",
          color: "var(--civ-text, #fff)", fontSize: 14, fontWeight: 700,
          background: "var(--civ-surface-subtle, rgba(59,130,246,0.18))",
          border: "1px solid rgba(59,130,246,0.4)",
          marginTop: 8, width: "100%", boxSizing: "border-box",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26, height: 26, borderRadius: "50%",
            background: initial ? "linear-gradient(135deg, #F59E0B, #FBBF24)" : "rgba(0,0,0,0.18)",
            color: initial ? "#0F172A" : "var(--civ-text, #fff)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontWeight: 800, fontSize: 13, flexShrink: 0,
          }}
        >{initial || "👤"}</span>
        <span style={{ flex: 1 }}>Mon Compte</span>
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAuthOpen(true)}
        data-testid="sidebar-connexion-btn"
        className="civ-sidebar-account-btn aya-account-btn-anon"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 12px", borderRadius: 10,
          /* v2.19.29e — En light : fond orange saturé + texte blanc.
             En dark : fond orange pâle + texte blanc.
             Géré par .aya-account-btn-anon dans App.css. */
          fontSize: 14, fontWeight: 700, cursor: "pointer",
          marginTop: 8, width: "100%", boxSizing: "border-box",
          fontFamily: "inherit", textAlign: "left",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26, height: 26, borderRadius: "50%",
            background: "rgba(255,255,255,0.20)",
            color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, flexShrink: 0,
          }}
        >👤</span>
        <span style={{ flex: 1 }}>Connexion</span>
      </button>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}

// v2.19.29d — Toggle thème iOS-like compact (~36px).
// Tap n'importe où sur le bouton → onToggle().
function ThemeToggleCompact({ theme, onToggle }) {
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="sidebar-theme-toggle"
      aria-label={`Basculer en mode ${isDark ? "clair" : "sombre"}`}
      className="aya-theme-toggle"
      style={{
        marginTop: 6,
        width: "100%", boxSizing: "border-box",
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px", borderRadius: 10,
        /* v2.19.29e — Texte foncé en light, clair en dark. Géré par .aya-theme-toggle dans App.css. */
        fontSize: 13, fontWeight: 600,
        cursor: "pointer", fontFamily: "inherit",
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 15, width: 18, textAlign: "center" }}>
        {isDark ? "🌙" : "☀️"}
      </span>
      <span style={{ flex: 1, textAlign: "left" }}>Thème</span>
      <span
        aria-hidden="true"
        style={{
          width: 40, height: 22, borderRadius: 11, position: "relative",
          background: isDark ? "#1F2937" : "#D1D5DB",
          transition: "background 200ms ease",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute", top: 2, left: 2,
            width: 18, height: 18, borderRadius: "50%",
            background: isDark ? "#FBBF24" : "#FFFFFF",
            boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
            transform: isDark ? "translateX(18px)" : "translateX(0)",
            transition: "transform 200ms cubic-bezier(0.4,0,0.2,1), background 200ms ease",
          }}
        />
      </span>
    </button>
  );
}




const MODE_OPTIONS = [
  { value: "casual", label: "Casual", dot: "🟢" },
  { value: "street", label: "Nouchi", dot: "🔴" },
  { value: "business", label: "Business", dot: "🔵" },
];

const WEB_SEARCH_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "on", label: "Toujours" },
  { value: "off", label: "Jamais" },
];

function SegmentGroup({ value, options, onChange, testidPrefix }) {
  return (
    <div className="civ-segment-group" data-testid={testidPrefix ? `${testidPrefix}-group` : undefined}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`civ-segment-btn ${value === o.value ? "is-active" : ""}`}
          onClick={() => onChange(o.value)}
          data-testid={testidPrefix ? `${testidPrefix}-${o.value}` : undefined}
        >
          {o.dot ? <span className="civ-segment-dot">{o.dot}</span> : null}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

function SidebarSettingsPanel({
  open, onToggle,
  mode, onModeChange,
  voiceOutputEnabled, onVoiceOutputToggle,
  webSearchMode, onWebSearchModeChange,
  continuousMode, onContinuousModeToggle,
  responseLang, onResponseLangChange,
}) {
  return (
    <div className={`civ-settings ${open ? "is-open" : ""}`} data-testid="sidebar-settings">
      <button
        type="button"
        className="civ-settings-toggle"
        onClick={onToggle}
        aria-expanded={open}
        data-testid="sidebar-settings-toggle"
      >
        <span>⚙️ Paramètres</span>
        <span className="civ-settings-chevron">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="civ-settings-body" data-testid="sidebar-settings-body">
          <div className="civ-settings-row">
            <div className="civ-settings-label">🎭 Mode</div>
            <SegmentGroup
              value={mode}
              options={MODE_OPTIONS}
              onChange={onModeChange}
              testidPrefix="settings-mode"
            />
          </div>

          <div className="civ-settings-row">
            <div className="civ-settings-label">🔊 Voix</div>
            <SegmentGroup
              value={voiceOutputEnabled ? "on" : "off"}
              options={[
                { value: "on", label: "Activée" },
                { value: "off", label: "Désactivée" },
              ]}
              onChange={(v) => onVoiceOutputToggle(v === "on")}
              testidPrefix="settings-voice"
            />
          </div>

          <div className="civ-settings-row">
            <div className="civ-settings-label">🌐 Recherche web</div>
            <SegmentGroup
              value={webSearchMode}
              options={WEB_SEARCH_OPTIONS}
              onChange={onWebSearchModeChange}
              testidPrefix="settings-web-search"
            />
          </div>

          {typeof onResponseLangChange === "function" && (
            <div className="civ-settings-row">
              <div className="civ-settings-label">🌍 Langue de réponse</div>
              <SegmentGroup
                value={responseLang || "auto"}
                options={[
                  { value: "auto", label: "Auto" },
                  { value: "fra", label: "🇫🇷 FR" },
                  { value: "dyu", label: "🇨🇮 Dioula" },
                ]}
                onChange={onResponseLangChange}
                testidPrefix="settings-response-lang"
              />
            </div>
          )}

          <div className="civ-settings-row">
            <div className="civ-settings-label">🔁 Mode vocal continu</div>
            <SegmentGroup
              value={continuousMode ? "on" : "off"}
              options={[
                { value: "off", label: "Off" },
                { value: "on", label: "On" },
              ]}
              onChange={(v) => onContinuousModeToggle(v === "on")}
              testidPrefix="settings-continuous"
            />
          </div>
        </div>
      )}
    </div>
  );
}
